// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// Ensure service worker is registered and return the registration
async function ensureServiceWorkerRegistered(): Promise<ServiceWorkerRegistration> {
  // Check if already registered
  const existingReg = await navigator.serviceWorker.getRegistration();
  if (existingReg) {
    return navigator.serviceWorker.ready;
  }

  // Register now if not already registered
  await navigator.serviceWorker.register('/service-worker.js');
  return navigator.serviceWorker.ready;
}

async function subscribeUser(): Promise<boolean> {
  try {
    console.log('process.env.VAPID_PUBLIC_KEY', process.env.VAPID_PUBLIC_KEY);

    // Ensure service worker is registered first
    const registration = await ensureServiceWorkerRegistered();

    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: process.env.VAPID_PUBLIC_KEY,
    });

    if (!subscription) {
      console.error('Failed to get push subscription');
      return false;
    }

    console.log('Push subscription obtained:', subscription);

    // Send subscription to backend
    const response = await fetch('/api/notifications_subscriptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription }),
    });

    if (!response.ok) {
      console.error('Failed to save subscription to backend:', response.status);
      return false;
    }

    console.log('User subscribed successfully');
    return true;
  } catch (err) {
    console.error('Error subscribing user:', err);
    return false;
  }
}

// Check if notifications are supported (including iOS PWA check)
export function areNotificationsSupported(): boolean {
  try {
    // On iOS Safari (non-PWA), Notification may not exist or accessing it may throw
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  } catch {
    return false;
  }
}

// Get current notification permission status
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  try {
    if (!areNotificationsSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  } catch {
    return 'unsupported';
  }
}

// Request notification permission - must be called from user interaction (click/tap)
export async function requestNotificationPermission(): Promise<boolean> {
  if (!areNotificationsSupported()) {
    console.error('Notifications not supported');
    return false;
  }

  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    console.log('Notification permission granted.');
    const subscribed = await subscribeUser();
    if (!subscribed) {
      console.error('Permission granted but subscription failed');
    }
    return subscribed;
  } else {
    console.error('Notification permission denied.');
    return false;
  }
}

export function registerServiceWorker(config?: Config): any {
  const isTestEnv = process.env.NODE_ENV === 'test';
  if (!isTestEnv && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://bit.ly/CRA-PWA',
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });

    // Auto-subscribe if permission already granted (e.g., returning user)
    if (getNotificationPermission() === 'granted') {
      navigator.serviceWorker.ready.then(() => {
        subscribeUser().catch((err) =>
          console.error('Auto-subscribe failed:', err),
        );
      });
    }
    // NOTE: Do NOT auto-request permission here - iOS requires user interaction (tap/click)
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.',
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.',
      );
    });
}

export function unregister(): any {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
